import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Tag, Bell } from 'lucide-react';
import { projects } from '../data/projects';
import { ProjectCard } from '../components/common/ProjectCard';
import { EnquiryForm } from '../components/common/EnquiryForm';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

export default function UpcomingPage() {
  const upcomingProjects = projects.filter((p) => p.status === 'Upcoming');

  return (
    <div data-testid="upcoming-page" className="pt-20">
      {/* Header */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#F0F8FF] to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <Badge className="bg-amber-100 text-amber-700 px-4 py-2">
              <Bell className="w-4 h-4 mr-2" />
              New Launches
            </Badge>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 font-outfit">
            Upcoming Projects
          </h1>
          <p className="text-lg text-slate-600 font-manrope mt-4 max-w-2xl">
            Be the first to know about our new launches. Pre-book now and avail exclusive 
            early bird offers and discounts.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F0F8FF] flex items-center justify-center flex-shrink-0">
                <Tag className="w-6 h-6 text-[#47ace2]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 font-outfit">Early Bird Pricing</h3>
                <p className="text-slate-600 text-sm font-manrope mt-1">
                  Get exclusive pre-launch prices before public release
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F0F8FF] flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-[#47ace2]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 font-outfit">Flexible Payment Plans</h3>
                <p className="text-slate-600 text-sm font-manrope mt-1">
                  Easy construction-linked payment options
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F0F8FF] flex items-center justify-center flex-shrink-0">
                <ArrowRight className="w-6 h-6 text-[#47ace2]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 font-outfit">Priority Selection</h3>
                <p className="text-slate-600 text-sm font-manrope mt-1">
                  Choose your preferred unit before others
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {upcomingProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingProjects.map((project) => (
                <div key={project.id} className="relative">
                  <div className="absolute -top-3 -right-3 z-10">
                    <Badge className="bg-amber-500 text-white shadow-lg">
                      Pre-Launch
                    </Badge>
                  </div>
                  <ProjectCard project={project} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 font-outfit mb-2">
                Coming Soon
              </h3>
              <p className="text-slate-600 font-manrope mb-6">
                We're working on exciting new projects. Stay tuned!
              </p>
              <Link to="/projects">
                <Button className="bg-[#47ace2] hover:bg-[#1f7db3] rounded-full">
                  View Current Projects
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Pre-Register CTA */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
                Don't Miss Out
              </span>
              <h2 className="text-3xl md:text-4xl font-light tracking-tight font-outfit mt-4 mb-6">
                Register for Early Access
              </h2>
              <p className="text-slate-300 font-manrope text-lg mb-8">
                Be among the first to receive updates about our upcoming projects. 
                Get exclusive access to pre-launch events and special offers.
              </p>
              <ul className="space-y-3">
                {['Exclusive pre-launch prices', 'Priority unit selection', 'Special payment plans', 'Site visit invitations'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300 font-manrope">
                    <div className="w-5 h-5 rounded-full bg-[#47ace2]/20 flex items-center justify-center">
                      <ArrowRight className="w-3 h-3 text-[#47ace2]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-slate-900 font-outfit mb-6">
                Pre-Register Now
              </h3>
              <EnquiryForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
